import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAccountsByUserId } from '../api/account_api';
import { useAuth } from './authProvider';

/**
 * AccountProvider - Centralized Account State Management
 *
 * Provides global account state across all dashboards.
 * Handles account fetching, selection, and persistence.
 *
 * Features:
 * - Single API call on app mount (performance optimization)
 * - Automatic localStorage persistence
 * - Cross-tab synchronization via storage events
 * - Comprehensive error handling
 * - Backward compatible with existing components
 */

const AccountContext = createContext();

export const useAccount = () => {
    const context = useContext(AccountContext);
    if (!context) {
        throw new Error('useAccount must be used within AccountProvider');
    }
    return context;
};

export const AccountProvider = ({ children }) => {
    const { token, getCurrentUser } = useAuth();
    // Compute userId from getCurrentUser() function (not from cached value)
    const currentUser = getCurrentUser();
    const userId = currentUser?.userId || currentUser?.id;

    // Debug: Log when token or userId changes
    useEffect(() => {
        console.log('[AccountProvider] 🔍 Token/User changed - token:', token ? 'present' : 'missing', 'userId:', userId || 'missing');
    }, [token, userId]);

    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount_] = useState(() => {
        // Initialize from localStorage on mount
        const saved = localStorage.getItem('selectedAccount');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                console.log('[AccountProvider] 💾 Restored from localStorage:', parsed.accountNumber || parsed.account_number);
                return parsed;
            } catch (err) {
                console.error('[AccountProvider] ❌ Failed to parse saved account:', err);
                return null;
            }
        }
        return null;
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch accounts on mount and when token/userId changes
    useEffect(() => {
        const fetchAccounts = async () => {
            console.log('[AccountProvider] 🔄 useEffect triggered - token:', token ? 'present' : 'missing', 'userId:', userId || 'missing');

            if (!userId || !token) {
                console.log('[AccountProvider] ⚠️ Missing userId or token, skipping fetch');
                setLoading(false);
                return;
            }

            try {
                console.log('[AccountProvider] 🔄 Fetching accounts for userId:', userId);
                setLoading(true);
                setError(null);

                const response = await getAccountsByUserId(token, userId);
                console.log('[AccountProvider] 📥 API Response:', response);

                // Handle multiple API response formats (robust parsing)
                let accountList = [];
                if (Array.isArray(response)) {
                    accountList = response;
                } else if (response?.success && Array.isArray(response?.data)) {
                    accountList = response.data;
                } else if (Array.isArray(response?.data)) {
                    accountList = response.data;
                } else if (response?.data && !Array.isArray(response.data)) {
                    // Single account wrapped in object
                    accountList = [response.data];
                }

                console.log('[AccountProvider] ✅ Loaded', accountList.length, 'accounts');
                setAccounts(accountList);

                // Auto-select account logic
                if (accountList.length === 0) {
                    console.log('[AccountProvider] ⚠️ No accounts found for user');
                    setSelectedAccount_(null);
                    localStorage.removeItem('selectedAccount');
                } else if (!selectedAccount) {
                    // No saved account - select first
                    const firstAccount = accountList[0];
                    console.log('[AccountProvider] 🎯 Auto-selecting first account:', firstAccount.accountNumber || firstAccount.account_number);
                    setSelectedAccount_(firstAccount);
                    localStorage.setItem('selectedAccount', JSON.stringify(firstAccount));
                } else {
                    // Verify saved account still exists
                    const savedAccountNumber = selectedAccount.accountNumber || selectedAccount.account_number;

                    // Add null check before toString()
                    if (savedAccountNumber) {
                        const exists = accountList.find(acc => {
                            const accNumber = acc.accountNumber || acc.account_number;
                            return accNumber && accNumber.toString() === savedAccountNumber.toString();
                        });

                        if (exists) {
                            console.log('[AccountProvider] 📌 Using saved account:', savedAccountNumber);
                            // Update with fresh data from API
                            setSelectedAccount_(exists);
                            localStorage.setItem('selectedAccount', JSON.stringify(exists));
                        } else {
                            console.log('[AccountProvider] ⚠️ Saved account not found, selecting first');
                            const firstAccount = accountList[0];
                            setSelectedAccount_(firstAccount);
                            localStorage.setItem('selectedAccount', JSON.stringify(firstAccount));
                        }
                    } else {
                        // No valid saved account number, select first
                        console.log('[AccountProvider] ⚠️ Saved account has no valid number, selecting first');
                        const firstAccount = accountList[0];
                        setSelectedAccount_(firstAccount);
                        localStorage.setItem('selectedAccount', JSON.stringify(firstAccount));
                    }
                }
            } catch (err) {
                console.error('[AccountProvider] ❌ Error fetching accounts:', err);
                setError(err.message || 'Failed to fetch accounts');
                setAccounts([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAccounts();
    }, [token]); // Token change triggers re-fetch, userId computed from token

    // Public API: Set selected account
    const setSelectedAccount = useCallback((account) => {
        if (!account) {
            console.log('[AccountProvider] 📌 Clearing account selection');
            setSelectedAccount_(null);
            localStorage.removeItem('selectedAccount');
            return;
        }

        const accountNumber = account.accountNumber || account.account_number;
        console.log('[AccountProvider] 📌 Account changed to:', accountNumber);

        setSelectedAccount_(account);
        localStorage.setItem('selectedAccount', JSON.stringify(account));

        // Broadcast storage event to other tabs/windows
        window.dispatchEvent(new StorageEvent('storage', {
            key: 'selectedAccount',
            newValue: JSON.stringify(account),
            oldValue: localStorage.getItem('selectedAccount'),
            storageArea: localStorage
        }));
    }, []);

    // Listen for account changes from other tabs/windows
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'selectedAccount') {
                if (e.newValue) {
                    try {
                        const newAccount = JSON.parse(e.newValue);
                        const accountNumber = newAccount.accountNumber || newAccount.account_number;
                        console.log('[AccountProvider] 🔄 Account changed in another tab:', accountNumber);
                        setSelectedAccount_(newAccount);
                    } catch (err) {
                        console.error('[AccountProvider] ❌ Error parsing account from storage:', err);
                    }
                } else {
                    console.log('[AccountProvider] 🔄 Account cleared in another tab');
                    setSelectedAccount_(null);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Helper: Get account number (handles both field name formats)
    const getAccountNumber = useCallback(() => {
        if (!selectedAccount) return null;
        return selectedAccount.accountNumber ||
               selectedAccount.account_number ||
               null;
    }, [selectedAccount]);

    // Helper: Refresh accounts from API
    const refetchAccounts = useCallback(async () => {
        if (!userId || !token) {
            console.log('[AccountProvider] ⚠️ Cannot refetch: missing userId or token');
            return;
        }

        try {
            console.log('[AccountProvider] 🔄 Manually refetching accounts...');
            setLoading(true);
            const response = await getAccountsByUserId(token, userId);

            let accountList = [];
            if (Array.isArray(response)) {
                accountList = response;
            } else if (response?.success && Array.isArray(response?.data)) {
                accountList = response.data;
            } else if (Array.isArray(response?.data)) {
                accountList = response.data;
            }

            setAccounts(accountList);
            console.log('[AccountProvider] ✅ Refetch complete:', accountList.length, 'accounts');
        } catch (err) {
            console.error('[AccountProvider] ❌ Refetch failed:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [userId, token]);

    // Context value
    const value = {
        // State
        accounts,
        selectedAccount,
        loading,
        error,

        // Actions
        setSelectedAccount,
        refetchAccounts,

        // Helpers
        getAccountNumber,
    };

    return (
        <AccountContext.Provider value={value}>
            {children}
        </AccountContext.Provider>
    );
};

export default AccountProvider;
